import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';

import { AuthProvider, useAuth } from '@/auth/auth-provider';
import { consumeLoginRedirect } from '@/auth/token-handoff';
import SplashScreen from '@/components/splash-screen';
import { TanstackProvider } from '@/providers/tanstack-provider';
import { routes } from '@/routes';

// Import order matters: index.css's overrides must come after @azelis/ui's
// base styles to win the cascade.
import '@azelis/ui/styles.css';
import './index.css';

const router = createRouter({
  routeTree: routes,
  defaultPreload: 'intent',
  scrollRestoration: true,
  context: {
    auth: undefined, // set for real once wrapped in AuthProvider, below
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter<typeof routes>>;
  }
}

const App = () => {
  const auth = useAuth();
  if (auth.status === 'loading') {
    return <SplashScreen />;
  }
  return (
    <RouterProvider
      router={router}
      context={{ auth }}
    />
  );
};

function main() {
  if (consumeLoginRedirect() === 'handled-in-popup') {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const rootElement = document.getElementById('root')!;
  if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <StrictMode>
        <AuthProvider>
          <TanstackProvider>
            <App />
          </TanstackProvider>
        </AuthProvider>
      </StrictMode>
    );
  }
}

main();
